// context/AssessmentContext.jsx
// Manages assessment state throughout the user journey
// Persists session token in localStorage for resume functionality

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AssessmentContext = createContext(null);

export const AssessmentProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(
    localStorage.getItem('esb_session') || null
  );
  const [submissionId, setSubmissionId] = useState(
    localStorage.getItem('esb_submission') || null
  );
  const [answers, setAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('unpaid');

  // Resume session if token exists
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sessionToken && !user) {
      resumeSession();
    }
  }, [sessionToken]);

  const resumeSession = async () => {
    try {
      const response = await axios.get(`/assessment/session/${sessionToken}`);
      if (response.data.success) {
        setUser(response.data.data.user);
        // In the new flow payment happens AFTER assessment
        // So unpaid is fine - user can proceed to assessment
        setPaymentStatus(response.data.data.user.paymentStatus);
        if (response.data.data.submission) {
          setAnswers(response.data.data.submission.answers || []);
          setCurrentQuestion(response.data.data.submission.currentQuestion || 0);
          setSubmissionId(response.data.data.submission.id);
        }
      }
    } catch (err) {
      clearSession();
    }
  };

  const captureUser = async (userData) => {
    setLoading(true);
    try {
      const response = await axios.post('/assessment/capture', userData);
      if (response.data.success) {
        const { userId, sessionToken: token, paymentStatus: status, accessType } = response.data.data;
        setSessionToken(token);
        setPaymentStatus(status);
        localStorage.setItem('esb_session', token);
        if (accessType) localStorage.setItem('esb_access_type', accessType);
        setUser({ id: userId, accessType, ...userData });
        return { success: true, data: response.data.data };
      }
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Something went wrong' };
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = (questionNumber, score) => {
    setAnswers(prev => {
      const existing = prev.findIndex(a => a.questionNumber === questionNumber);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { questionNumber, score };
        return updated;
      }
      return [...prev, { questionNumber, score }];
    });
  };

  const saveProgress = async (newAnswers, question) => {
    if (!sessionToken || !user) return;
    try {
      const response = await axios.post('/assessment/save-progress', {
        userId: user.id,
        sessionToken,
        answers: newAnswers || answers,
        currentQuestion: question || currentQuestion,
      });
      if (response.data.success) {
        setSubmissionId(response.data.data.submissionId);
        localStorage.setItem('esb_submission', response.data.data.submissionId);
      }
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };

  const submitAssessment = async (allAnswers) => {
    setLoading(true);
    try {
      const response = await axios.post('/assessment/submit', {
        userId: user.id,
        sessionToken,
        answers: allAnswers || answers,
      });
      if (response.data.success) {
        setSubmissionId(response.data.data.submissionId);
        localStorage.setItem('esb_submission', response.data.data.submissionId);
        return { success: true, data: response.data.data };
      }
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Submission failed' };
    } finally {
      setLoading(false);
    }
  };

  const clearSession = () => {
    setUser(null);
    setSessionToken(null);
    setSubmissionId(null);
    setAnswers([]);
    setCurrentQuestion(0);
    setPaymentStatus('unpaid');
    localStorage.removeItem('esb_session');
    localStorage.removeItem('esb_submission');
  };

  const value = {
    user,
    sessionToken,
    submissionId,
    answers,
    currentQuestion,
    loading,
    paymentStatus,
    setUser,
    setPaymentStatus,
    setCurrentQuestion,
    captureUser,
    saveAnswer,
    saveProgress,
    submitAssessment,
    clearSession,
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) throw new Error('useAssessment must be used within AssessmentProvider');
  return context;
};

export default AssessmentContext;
